import ReportApi from 'api/ReportApi';
import LoadingMessage from 'components/LoadingMessage';
import PdfReport from 'components/PdfReport';
import {ScrollToTop} from 'components/ScrollToTop';
import {toggleReportGenerationErrorAlert} from 'ducks/alerts';
import {error as finaliseError} from 'ducks/finalise';
import {updateReportHash} from 'ducks/reportHash';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import {reportTypeToFallbackPage} from 'lib/RoutesUtil';
import ValidationUtils from 'lib/ValidationUtils';
import hash from 'object-hash';
import {Container} from '@mui/material';
import PropTypes from 'prop-types';
import {Component} from 'react';
import {push} from 'react-router-redux';
import {Button, Icon} from 'semantic-ui-react';
import connected from '../connected';
import './FinalisePage.scss';

export class FinalisePage extends Component {

    static propTypes = {
        comparableSales: PropTypes.object.isRequired,
        comparableListings: PropTypes.object.isRequired,
        featureFlags: PropTypes.object.isRequired,
        indicativeSellingPrice: PropTypes.object.isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string,
        }).isRequired,
        propertyDetail: PropTypes.object.isRequired,
        reportHash: PropTypes.string,
        reportSections: PropTypes.object.isRequired,
        reportType: PropTypes.shape({value: PropTypes.string.isRequired}).isRequired,
        reportUrl: PropTypes.string,
        userDetails: PropTypes.object.isRequired,
        theme: PropTypes.object.isRequired,
        finalise: PropTypes.shape({
            status: PropTypes.string,
            error: PropTypes.any,
        }),
        dispatch: PropTypes.func.isRequired,
    };

    componentDidMount() {
        const {
            dispatch,
            reportHash,
            reportType: {value: reportTypeValue},
            propertyDetail: {singleLineAddress},
            location: {pathname},
        } = this.props;

        // avoid crashing when property not loaded
        if (!singleLineAddress) {
            const propertyId = /\/properties\/(\d+)\/.*/.exec(pathname)[1];
            dispatch(push(`/properties/${propertyId}`));
            return;
        }

        if (ValidationUtils.getReportErrorMessages(this.props).length > 0) {
            this.returnToCustomisePage();
        }

        let currentReportHash = this.hashedReportValues();
        if (currentReportHash !== reportHash) {
            dispatch(updateReportHash(currentReportHash));
            ReportApi.fetchReport(reportTypeValue);
            AnalyticsGateway.sendEvent({
                eventName: 'View Finalise Page',
                properties: [
                    {label: 'Page', value: 'Preview Report'},
                    {label: 'Report Type', value: reportTypeValue},
                ],
            });
        }
    }

    hashedReportValues = () => {
        const {reportType: {value: reportTypeValue}} = this.props;
        return hash.MD5(ReportApi.getPayload(reportTypeValue));
    };

    returnToCustomisePage = () => {
        const {
            dispatch,
            reportType: {value: reportTypeValue},
            location: {pathname},
        } = this.props;

        dispatch(toggleReportGenerationErrorAlert(true));
        const propertyId = /\/properties\/(\d+)\/.*/.exec(pathname)[1];
        const fallbackPage = reportTypeToFallbackPage[reportTypeValue];
        dispatch(push(`/properties/${propertyId}/${reportTypeValue}/${fallbackPage}`));
    };

    onPdfError = (pdfError) => {
        const {dispatch} = this.props;
        dispatch(finaliseError({message: (pdfError && pdfError.message) || 'Failed to load PDF file.'}));
    };

    loadingComponent = (header) => {
        const {propertyDetail: {singleLineAddress}} = this.props;
        return (
            <div className="loading-frame" style={{
                background: 'linear-gradient(#ffffff, #f5f7f8)',
                padding: '50px',
                height: '100mm',
                marginBottom: '60px',
            }}>
                <LoadingMessage
                    header={header}
                    style={{
                        margin: '0 auto',
                        maxWidth: '210mm',
                    }}
                >
                    {singleLineAddress}
                </LoadingMessage>
            </div>
        );
    };

    render() {
        const {
            propertyDetail: {singleLineAddress},
            reportType: {value: reportTypeValue},
            reportUrl,
            finalise: {error},
            featureFlags: {propertyPriceStatement = false} = {},
        } = this.props;

        // PPS is still reported internally as the 'soi' report type; only the displayed
        // description changes when the flag is on.
        const reportDescription = propertyPriceStatement && reportTypeValue === 'soi'
            ? 'Property Price Statement'
            : AnalyticsGateway.reportDescription(reportTypeValue);
        const generatingHeader = `Generating ${reportDescription}`;
        const loadingHeader = `Loading ${reportDescription} PDF`;

        return (
            (singleLineAddress || null) &&
            <div className="finalise-page">
                <Container>
                    {error && (
                        <div className="error-message">
                            <Icon name="ambulance" size="massive"/>
                            <span>An unexpected error has occurred.</span>
                            <Button onClick={() => ReportApi.fetchReport(reportTypeValue)}>
                                Retry
                            </Button>
                        </div>
                    )}
                    {reportUrl && !error ? (
                        <PdfReport reportType={reportTypeValue}
                                   singleLineAddress={singleLineAddress}
                                   url={reportUrl}
                                   loading={this.loadingComponent(loadingHeader)}
                                   onPdfError={this.onPdfError}
                        />
                    ) : !error && (this.loadingComponent(generatingHeader))
                    }
                    {reportUrl && !error && <div style={{paddingTop: '3.5em'}}/>}
                    <ScrollToTop/>
                </Container>
            </div>

        );
    }
}

export default connected(FinalisePage);
